<!--
  SupportListSection - Section with title, bullet lists, and CTA

  Props:
  - backgroundColor (String): Background color of the section (default: '#FFFFFF')
  - title (String): Section title
  - titleHighlight (String): Highlighted portion of title
  - titleHighlightGradientFrom (String): Start color of title highlight gradient (default: '#11D2D4')
  - titleHighlightGradientTo (String): End color of title highlight gradient (default: '#0C7475')
  - titleColor (String): Color of title text (default: 'text-blue-dark')
  - introText (Array): Array of intro paragraph strings
  - sectionTitle (String): Title for bullet list section
  - bulletList (Array): Array of bullet point text strings
  - bulletColor (String): Color of bullet points (default: 'text-blue')
  - textColor (String): Color of text content (default: 'text-gray-700')
  - subsectionTitle (String): Title for subsection
  - subsectionText (String): Text for subsection
  - ctaText (String): CTA button text
  - ctaLink (String): CTA button link
  - showCta (Boolean): Whether to show CTA button (default: true)
  - buttonGradientFrom (String): Start color of button gradient (default: '#11D2D4')
  - buttonGradientTo (String): End color of button gradient (default: '#0C7475')
  - fallbackCtaText (String): Fallback CTA text when showCta is false
  - fallbackCtaBackgroundColor (String): Background color for fallback CTA (default: '#1E40AF')
-->
<template>
    <section class="py-20" :style="{ backgroundColor }">
        <div class="container mx-auto px-4">
            <h2 class="w-auto mx-auto max-w-4xl text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-center" :class="titleColor">
                <span v-html="renderTitle()"></span>
            </h2>
            <p v-for="(text, index) in introText" :key="`intro-${index}`" class="text-lg mb-4" :class="textColor">{{ text }}</p>

            <div v-if="sectionTitle || bulletList.length" class="mb-8 px-4">
                <h3 v-if="sectionTitle" class="text-xl font-bold mb-4" :class="titleColor">{{ sectionTitle }}</h3>
                <ul class="space-y-0" :class="textColor">
                    <li v-for="(item, index) in bulletList" :key="index" class="flex items-start gap-3">
                        <span class="mt-1" :class="bulletColor">•</span>
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </div>

            <div v-if="subsectionTitle || subsectionText" class="mb-8">
                <h3 v-if="subsectionTitle" class="text-xl font-bold mb-4" :class="titleColor">{{ subsectionTitle }}</h3>
                <p v-if="subsectionText" :class="textColor">
                    {{ subsectionText }}
                </p>
            </div>

            <div v-if="ctaText" class="text-center mt-10">
                <router-link
                    v-if="showCta"
                    :to="ctaLink"
                    class="inline-flex items-center gap-2 text-white px-8 py-4 rounded-full font-semibold transition hover:opacity-90"
                    :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                >
                    {{ ctaText }}
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </router-link>
                <div v-else class="text-white px-2 lg:px-6 py-3 rounded-full font-semibold transition text-xs lg:text-md" :style="{ backgroundColor: fallbackCtaBackgroundColor }">
                    {{ fallbackCtaText }}
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    introText: {
        type: Array,
        default: () => []
    },
    sectionTitle: {
        type: String,
        default: ''
    },
    bulletList: {
        type: Array,
        default: () => []
    },
    bulletColor: {
        type: String,
        default: 'text-blue'
    },
    textColor: {
        type: String,
        default: 'text-gray-700'
    },
    subsectionTitle: {
        type: String,
        default: ''
    },
    subsectionText: {
        type: String,
        default: ''
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    buttonGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    buttonGradientTo: {
        type: String,
        default: '#0C7475'
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const highlightRegex = new RegExp(`(${props.titleHighlight})`, 'g');
    return props.title.replace(
        highlightRegex,
        `<span class="bg-gradient-to-r text-transparent bg-clip-text" style="background: linear-gradient(to right, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">$1</span>`
    );
};
</script>
